import { Note } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

export interface SeedFlexibleNoteBodyParams {
  /** The current note body (may be undefined for a brand new note). */
  body: Note['body'] | undefined
  /** Previous note's format, if one is available to carry forward. */
  previousNoteFormat?: string
  /** Therapy type derived from the appointment's program, if resolvable. */
  therapyType?: string
  /** Resolved provider name (fellow/supervisor aware) to prefill the attestation signature. */
  resolvedProviderName?: string
}

/**
 * Computes the seed values that should be merged into a DA flexible note's body so that
 * sensible defaults are prefilled for the provider. Each field is only seeded when it is
 * missing (or, for therapyType, out of sync), so an existing provider-entered value is never
 * clobbered. typedSignature additionally treats an intentionally-cleared empty string as a
 * value to preserve (see the needsTypedSignature check below).
 *
 * Returns an object of only the fields that need seeding; returns an empty object when nothing
 * needs to change so callers can skip creating a new note reference.
 */
export const getSeededFlexibleNoteBody = ({
  body,
  previousNoteFormat,
  therapyType,
  resolvedProviderName,
}: SeedFlexibleNoteBodyParams): {
  noteFormat?: string
  therapyType?: string
  typedSignature?: string
} => {
  const needsNoteFormat = Boolean(previousNoteFormat && !body?.noteFormat)
  const needsTherapyType = Boolean(therapyType && body?.therapyType !== therapyType)
  // Signature is a deliberate attestation, so honor an intentional clear: only seed when the field
  // is genuinely absent (undefined), NOT when it is an empty string. This diverges from the falsy
  // noteFormat check above on purpose — re-seeding a signature the provider cleared would silently
  // re-attest for them on the next remount.
  const needsTypedSignature = Boolean(resolvedProviderName && body?.typedSignature === undefined)

  return {
    ...(needsNoteFormat && { noteFormat: previousNoteFormat }),
    ...(needsTherapyType && { therapyType }),
    ...(needsTypedSignature && { typedSignature: resolvedProviderName }),
  }
}
